import { useMemo } from 'react'
import { BigNumber, ethers } from 'ethers'
import {
    useBaseReads,
    useStakingDepositDetailsQueries,
    useStakingDepositsByOwnerQuery,
} from 'use-towns-client'
import { zeroAddress } from 'viem'

type UseStakingDepositsProps = {
    owner: `0x${string}` | undefined
    enabled?: boolean
}

export type DepositInfo = {
    depositId: bigint
    amount: ethers.BigNumber
    delegatee: `0x${string}`
    beneficiary: `0x${string}`
    commissionEarningPower: ethers.BigNumber
    pendingWithdrawal: ethers.BigNumber
}

export type SpaceDeposits = {
    space: `0x${string}`
    deposits: DepositInfo[]
    totalStaked: ethers.BigNumber
}

const emptySpaceDeposits: SpaceDeposits[] = []
const emptySpaceDepositInfos: DepositInfo[] = []

export const useStakingDeposits = ({ owner, enabled = true }: UseStakingDepositsProps) => {
    const { spaceDapp } = useBaseReads()

    // gather all deposit ids for the user
    const {
        data: depositIds,
        isLoading: isDepositIdsLoading,
        error: depositIdsError,
    } = useStakingDepositsByOwnerQuery({ owner, enabled })

    // get detailed info for each deposit
    const depositDetails = useStakingDepositDetailsQueries({
        depositIds,
        enabled,
    })

    const deposits = useMemo(() => {
        if (depositDetails.some((q) => q.isLoading)) {
            return emptySpaceDepositInfos as DepositInfo[]
        }
        return depositDetails
            .map((q) => q.data)
            .filter((deposit): deposit is DepositInfo => deposit !== null)
    }, [depositDetails])

    // group deposits by space and get rewards for each space
    const depositsGroupedBySpace = useMemo(() => {
        if (!enabled || !spaceDapp || !owner) {
            return emptySpaceDeposits
        }

        const spaceMap = new Map<string, DepositInfo[]>()

        for (const deposit of deposits) {
            const space = deposit.delegatee
            if (!space || space === zeroAddress) {
                // keep withdrawls separate
                continue
            }
            if (!spaceMap.has(space)) {
                spaceMap.set(space, [])
            }
            spaceMap.get(space)!.push(deposit)
        }

        const depositsGroupedBySpace: SpaceDeposits[] = []

        for (const [space, spaceDepositList] of spaceMap) {
            try {
                const totalStaked = spaceDepositList.reduce(
                    (sum, deposit) => sum.add(deposit.amount),
                    BigNumber.from(0),
                )

                depositsGroupedBySpace.push({
                    space: space as `0x${string}`,
                    deposits: spaceDepositList,
                    totalStaked,
                })
            } catch (error) {
                console.error(`Error fetching reward for space ${space}:`, error)
                const totalStaked = spaceDepositList.reduce(
                    (sum, deposit) => sum.add(deposit.amount),
                    BigNumber.from(0),
                )

                depositsGroupedBySpace.push({
                    space: space as `0x${string}`,
                    deposits: spaceDepositList,
                    totalStaked,
                })
            }
        }

        return depositsGroupedBySpace
    }, [spaceDapp, owner, deposits, enabled])

    const isLoading = isDepositIdsLoading || depositDetails.some((q) => q.isLoading)

    const totalStaked = useMemo(() => {
        return (
            depositsGroupedBySpace?.reduce(
                (sum, spaceDeposit) => sum.add(spaceDeposit.totalStaked),
                BigNumber.from(0),
            ) || BigNumber.from(0)
        )
    }, [depositsGroupedBySpace])

    const { depositsWithPendingWithdrawal, totalPendingWithdrawal } = useMemo(() => {
        const depositsWithPendingWithdrawal = deposits?.filter((deposit) =>
            deposit.pendingWithdrawal.gt(0),
        )

        const totalPendingWithdrawal =
            depositsWithPendingWithdrawal?.reduce(
                (sum, deposit) => sum.add(deposit.pendingWithdrawal),
                BigNumber.from(0),
            ) || BigNumber.from(0)

        return {
            depositsWithPendingWithdrawal,
            totalPendingWithdrawal,
        }
    }, [deposits])

    const data = useMemo(() => {
        return {
            groupedDeposits: depositsGroupedBySpace,
            depositsWithPendingWithdrawal,
            totalStaked,
            totalPendingWithdrawal,
        }
    }, [depositsGroupedBySpace, depositsWithPendingWithdrawal, totalStaked, totalPendingWithdrawal])

    return {
        data,
        isLoading,
        error: depositIdsError,
        isReady: !!spaceDapp,
    }
}
